/**
 * AnalyticsView.js — Industry-standard reservation & revenue analytics
 *
 * Sub-tabs:
 *   Overview  — KPIs: revenue, covers, turn time, tip %, check size, no-show/walk-out rates
 *   Menu      — Top items by revenue & count, food vs. drink split, comped items
 *   Servers   — Per-server: covers, revenue, avg check, tip %
 *   Patterns  — Day-of-week, meal period, booking window distribution
 *
 * Data source: restaurants_data/{restaurantId}/reservations
 * All computation is client-side from the same `date >= cutoff` query.
 * New fields used (written by POS since 2026-07):
 *   paidAt        — Firestore Timestamp, written by PaymentModal on full payment
 *   foodTotal     — number ($), food items subtotal at payment time
 *   drinkTotal    — number ($), drink items subtotal at payment time
 *   items[]       — { name, price, isDrinkItem, type, comped, quantity, selectedOptions }
 *   reservationServer — string, server display name
 *   requestDate   — Firestore Timestamp, when the booking was made
 *   dateOnly      — 'YYYY-MM-DD' string, reservation date
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ComposedChart, Cell,
} from 'recharts';
// ── Constants ─────────────────────────────────────────────────────────────────

const PAID_STATUSES   = new Set(['paid', 'bussing', 'completed', 'fullyPaid', 'comped']);
const OUTCOME_STATUSES = new Set(['paid', 'bussing', 'completed', 'fullyPaid', 'comped', 'noShow', 'walk_out']);

const RANGES = [
  { label: 'Today',   days: 0  },
  { label: '7 Days',  days: 7  },
  { label: '30 Days', days: 30 },
  { label: '90 Days', days: 90 },
  { label: 'Month',   days: -1 },
  { label: 'Year',    days: -2 },
];

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const COMPARE_PALETTE = ['#5f799a','#e11d48','#16a34a','#f97316','#9333ea','#0891b2','#d97706'];

const MEAL_PERIODS = [
  { label: 'Breakfast', start:  6, end: 11 },
  { label: 'Lunch',     start: 11, end: 14 },
  { label: 'Afternoon', start: 14, end: 17 },
  { label: 'Dinner',    start: 17, end: 22 },
  { label: 'Late',      start: 22, end: 30 }, // 30 = 6am next day
];

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Helpers ───────────────────────────────────────────────────────────────────

const avg = (arr) => arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : null;
const sum = (arr) => arr.reduce((s, x) => s + x, 0);
const fmt$ = (n) => n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n, dec = 1) => n == null ? '—' : `${n.toFixed(dec)}%`;
const fmtMins = (m) => {
  if (m == null) return '—';
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};
const itemPrice = (item) => {
  let total = parseFloat(item.price) || 0;
  for (const opt of (item.selectedOptions || [])) {
    for (const sel of (opt.selectedSelectors || [])) {
      total += parseFloat(sel.price) || 0;
    }
  }
  return total * (item.quantity || 1);
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

const MetricCard = ({ title, value, sub, dim, wide }) => (
  <div className={`bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-1 ${wide ? 'col-span-2' : ''}`}>
    <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide">{title}</p>
    {dim ? (
      <p className="text-sm text-neutral-400 italic">{value}</p>
    ) : (
      <>
        <p className="text-2xl font-bold text-neutral-900 leading-tight">{value}</p>
        {sub && <p className="text-xs text-neutral-400">{sub}</p>}
      </>
    )}
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mt-2 mb-1 px-0.5">{children}</p>
);

const SubTabBar = ({ tabs, active, onChange }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none -mx-1 px-1">
    {tabs.map((t) => (
      <button
        key={t}
        onClick={() => onChange(t)}
        className={`shrink-0 px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
          active === t
            ? 'bg-neutral-900 text-white'
            : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
        }`}
      >
        {t}
      </button>
    ))}
  </div>
);

const BarRow = ({ label, value, max, suffix = '', valueStr }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-neutral-500 w-20 shrink-0 truncate">{label}</span>
      <div className="flex-1 bg-neutral-100 rounded-full h-2">
        <div className="bg-neutral-800 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-neutral-700 w-16 text-right shrink-0">
        {valueStr ?? `${value}${suffix}`}
      </span>
    </div>
  );
};

const EmptyState = ({ msg = 'No data for this period' }) => (
  <p className="text-sm text-neutral-400 italic py-4 text-center">{msg}</p>
);

// ── Chart helpers ─────────────────────────────────────────────────────────────

const CHART_COLORS = {
  revenue: '#5f799a',
  pickup:  '#94a3b8',
  tips:    '#C9A84C',
  covers:  '#6366f1',
  food:    '#16a34a',
  drink:   '#0891b2',
};

const fmtAxis = (v) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`;

const ChartTooltipContent = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-lg px-4 py-3 text-xs">
      <p className="font-semibold text-neutral-700 mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 mt-0.5">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
          <span className="text-neutral-500 flex-1">{p.name}</span>
          <span className="font-semibold text-neutral-900">
            {formatter ? formatter(p.value, p.dataKey) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const ChartCard = ({ title, children, action }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4">
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{title}</p>
      {action}
    </div>
    {children}
  </div>
);

const ChartToggle = ({ options, value, onChange }) => (
  <div className="flex gap-1">
    {options.map(o => (
      <button
        key={o.value}
        onClick={() => onChange(o.value)}
        className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
          value === o.value ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// Compact inline comparison table — used per-section when comparison periods are active
const CmpTable = ({ metrics, primary, comparePeriods, primaryLabel }) => (
  <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-neutral-100 bg-neutral-50">
            <th className="px-3 py-2.5 text-left font-semibold text-neutral-400 uppercase tracking-wide">Metric</th>
            <th className="px-3 py-2.5 text-right font-bold whitespace-nowrap" style={{ color: COMPARE_PALETTE[0] }}>{primaryLabel}</th>
            {comparePeriods.map(cp => (
              <th key={cp.key} className="px-3 py-2.5 text-right font-bold whitespace-nowrap" style={{ color: cp.color }}>{cp.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metrics.map(m => (
            <tr key={m.label} className="border-b border-neutral-50 last:border-0 hover:bg-neutral-50">
              <td className="px-3 py-2 font-medium text-neutral-600 whitespace-nowrap">{m.label}</td>
              <td className="px-3 py-2 text-right font-semibold text-neutral-900 tabular-nums whitespace-nowrap">{m.fn(primary)}</td>
              {comparePeriods.map(cp => (
                <td key={cp.key} className="px-3 py-2 text-right text-neutral-600 tabular-nums whitespace-nowrap">
                  {cp.stats ? m.fn(cp.stats) : '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ── Data aggregation ──────────────────────────────────────────────────────────

/**
 * normalizeReservation — maps POS field names to what the analytics expects.
 *
 * POS writes:
 *   totalSales  — item subtotal (dollars), written by onReservationPaymentUpdated
 *   payments[]  — array with tipAmount per payment
 *
 * Analytics expects:
 *   total  — item subtotal
 *   tip    — sum of captured tip amounts
 */
const normalizeReservation = (r) => {
  const capturedPayments = (r.payments || []).filter(p => p.status === 'captured');
  // totalSales is written by onReservationPaymentUpdated only when items exist.
  // Fall back to summing the base payment amounts so tip% works even when the CF
  // hasn't run yet or the reservation had no priced items.
  const paymentAmountSum = capturedPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  return {
    ...r,
    total: r.totalSales || r.total || paymentAmountSum || 0,
    // r.tip is initialized to 0 on every reservation, so `!= null` is always true.
    // Only trust r.tip when it's actually > 0 (set by legacy path or explicit write).
    // Otherwise always derive from payments to get the real tip amount.
    tip: (r.tip != null && r.tip > 0)
      ? r.tip
      : capturedPayments.reduce((s, p) => s + (Number(p.tipAmount) || 0), 0),
  };
};

const aggregate = (reservations) => {
  const normalized = reservations.map(normalizeReservation);
  const paid     = normalized.filter(r => PAID_STATUSES.has(r.status));
  const outcomes = normalized.filter(r => OUTCOME_STATUSES.has(r.status));
  const noShows  = reservations.filter(r => r.status === 'noShow');
  const walkOuts = reservations.filter(r => r.status === 'walk_out');

  // ── Overview KPIs ──
  const withTurn    = paid.filter(r => r.seatedTime?.toMillis && r.paidAt?.toMillis);
  const turnMins    = avg(withTurn.map(r => (r.paidAt.toMillis() - r.seatedTime.toMillis()) / 60000));

  const withCheck   = paid.filter(r => (r.total || 0) > 0);
  const tipPct      = withCheck.length
    ? avg(withCheck.map(r => ((r.tip || 0) / r.total) * 100))
    : null;
  // Total tips collected in dollars — does not require total > 0
  const totalTips   = sum(paid.map(r => r.tip || 0));
  const tipsHaveData = paid.some(r => (r.tip || 0) > 0);

  const totalRevenue = sum(withCheck.map(r => r.total || 0));
  const foodRevenue  = sum(paid.filter(r => r.foodTotal > 0).map(r => r.foodTotal));
  const drinkRevenue = sum(paid.filter(r => r.drinkTotal > 0).map(r => r.drinkTotal));
  const hasFoodDrink = foodRevenue + drinkRevenue > 0;

  const covers      = sum(paid.map(r => Number(r.partySize) || 0));
  const withSize    = paid.filter(r => (Number(r.partySize) || 0) > 0);
  const avgParty    = avg(withSize.map(r => Number(r.partySize)));
  const avgCheck    = avg(withCheck.map(r => r.total));

  const completionRate = outcomes.length
    ? (paid.length / outcomes.length) * 100 : null;
  const noShowRate  = outcomes.length
    ? (noShows.length / outcomes.length) * 100 : null;
  const walkOutRate = outcomes.length
    ? (walkOuts.length / outcomes.length) * 100 : null;

  // Avg booking window (requestDate → dateOnly)
  const withWindow = paid.filter(r => r.requestDate?.toDate && r.dateOnly);
  const bookingWindowDays = withWindow.map(r => {
    const booked = r.requestDate.toDate();
    const [y, m, d] = r.dateOnly.split('-').map(Number);
    const resDate = new Date(y, m - 1, d);
    return Math.max(0, Math.round((resDate - booked) / 86400000));
  });
  const avgBookingWindow = avg(bookingWindowDays);

  // ── Menu ──
  const itemMap = {};      // name → { revenue, count, isDrink }
  const compedItems = [];
  for (const r of paid) {
    for (const item of (r.items || [])) {
      const name = item.name || 'Unknown';
      const price = itemPrice(item);
      if (item.comped) {
        compedItems.push({ name, price, reservationId: r.id });
        continue;
      }
      if (!itemMap[name]) itemMap[name] = { revenue: 0, count: 0, isDrink: !!item.isDrinkItem };
      itemMap[name].revenue += price;
      itemMap[name].count   += (item.quantity || 1);
    }
  }
  const menuItems = Object.entries(itemMap)
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.revenue - a.revenue);

  // ── Servers ──
  const serverMap = {};
  for (const r of paid) {
    const name = r.reservationServer || 'Unassigned';
    if (!serverMap[name]) serverMap[name] = { covers: 0, revenue: 0, tips: 0, checkCount: 0 };
    serverMap[name].covers += Number(r.partySize) || 0;
    if ((r.total || 0) > 0) {
      serverMap[name].revenue    += r.total;
      serverMap[name].tips       += r.tip || 0;
      serverMap[name].checkCount += 1;
    }
  }
  const servers = Object.entries(serverMap).map(([name, v]) => ({
    name,
    covers:   v.covers,
    revenue:  v.revenue,
    tips:     v.tips,
    avgCheck: v.checkCount > 0 ? v.revenue / v.checkCount : null,
    tipPct:   v.revenue > 0 ? (v.tips / v.revenue) * 100 : null,
  })).sort((a, b) => b.revenue - a.revenue);

  // ── Patterns — day of week ──
  const dowCounts  = Array(7).fill(0); // reservations
  const dowRevenue = Array(7).fill(0);
  for (const r of paid) {
    let dow = null;
    if (r.date?.toDate) dow = r.date.toDate().getDay();
    else if (r.dateOnly) dow = new Date(r.dateOnly + 'T12:00:00').getDay();
    if (dow != null) {
      dowCounts[dow]++;
      dowRevenue[dow] += r.total || 0;
    }
  }

  // ── Patterns — meal period ──
  const periodMap = {};
  for (const p of MEAL_PERIODS) periodMap[p.label] = { count: 0, revenue: 0 };
  for (const r of paid) {
    let hour = null;
    if (r.seatedTime?.toDate) hour = r.seatedTime.toDate().getHours();
    else if (r.date?.toDate)  hour = r.date.toDate().getHours();
    if (hour == null) continue;
    const h = hour < 6 ? hour + 24 : hour; // treat midnight-6am as "late"
    for (const p of MEAL_PERIODS) {
      if (h >= p.start && h < p.end) {
        periodMap[p.label].count++;
        periodMap[p.label].revenue += r.total || 0;
        break;
      }
    }
  }

  // ── Patterns — booking window buckets ──
  const windowBuckets = [
    { label: 'Same day',  min: 0, max: 0 },
    { label: '1–3 days',  min: 1, max: 3 },
    { label: '4–7 days',  min: 4, max: 7 },
    { label: '1–2 weeks', min: 8, max: 14 },
    { label: '2 wk+',     min: 15, max: Infinity },
  ].map(b => ({ ...b, count: 0 }));
  for (const d of bookingWindowDays) {
    const bucket = windowBuckets.find(b => d >= b.min && d <= b.max);
    if (bucket) bucket.count++;
  }

  return {
    // overview
    paid, outcomes, noShows, walkOuts,
    turnMins, turnSampleSize: withTurn.length,
    tipPct, tipSampleSize: withCheck.length, totalTips, tipsHaveData,
    totalRevenue, foodRevenue, drinkRevenue, hasFoodDrink,
    covers, avgParty, avgCheck, checkCount: withCheck.length,
    completionRate, noShowRate, walkOutRate,
    avgBookingWindow,
    // menu
    menuItems, compedItems,
    // servers
    servers,
    // patterns
    dowCounts, dowRevenue, periodMap, windowBuckets,
  };
};

// ── Pickup order aggregation ───────────────────────────────────────────────────

const aggregatePickup = (orders) => {
  const paid = orders.filter(o => o.paymentStatus === 'paid');
  const rev  = (o) => o.payment?.totalCaptured ?? o.total ?? 0;

  const totalRevenue = paid.reduce((s, o) => s + rev(o), 0);
  const totalTips    = paid.reduce((s, o) => s + (o.payment?.tipAmount ?? 0), 0);

  const hostOrders   = paid.filter(o => o.source !== 'widget');
  const widgetOrders = paid.filter(o => o.source === 'widget');

  const byMethod = {};
  for (const o of paid) {
    const type  = o.payment?.type || o.paymentMethod || 'unknown';
    const label = type === 'card' ? 'Card (Reader)' : type === 'cash' ? 'Cash' : type === 'phone' ? 'Phone' : type;
    if (!byMethod[label]) byMethod[label] = { count: 0, revenue: 0 };
    byMethod[label].count++;
    byMethod[label].revenue += rev(o);
  }

  const itemMap = {};
  for (const o of paid) {
    for (const item of (o.items || [])) {
      const name  = item.name || 'Unknown';
      const price = parseFloat(item.price || 0);
      if (!itemMap[name]) itemMap[name] = { revenue: 0, count: 0, isDrink: false };
      itemMap[name].revenue += price;
      itemMap[name].count++;
    }
  }

  return {
    count:        paid.length,
    totalRevenue,
    totalTips,
    hostCount:    hostOrders.length,
    hostRevenue:  hostOrders.reduce((s, o) => s + rev(o), 0),
    widgetCount:  widgetOrders.length,
    widgetRevenue: widgetOrders.reduce((s, o) => s + rev(o), 0),
    byMethod,
    itemMap,
  };
};

// ── Gift card aggregation ─────────────────────────────────────────────────────

const aggregateGiftCards = (cards) => {
  const purchased = cards.filter(c => !c.isFree && !c.isTest);
  const totalSold     = purchased.reduce((s, c) => s + (c.balanceCents || 0) / 100, 0);
  const totalRedeemed = purchased.reduce((s, c) => s + (c.totalRedeemedCents || 0) / 100, 0);
  return {
    count:       purchased.length,
    totalSold,
    totalRedeemed,
    outstanding: totalSold - totalRedeemed,
  };
};

// ── Sub-tab panels ────────────────────────────────────────────────────────────

const OverviewTab = ({ data, mergedRevChart = [], allSeries = [], isYearMode = false, comparePeriods = [], primaryLabel = 'Primary' }) => {
  const {
    paid, outcomes,
    turnMins, turnSampleSize,
    tipPct, tipSampleSize, totalTips, tipsHaveData,
    totalRevenue, foodRevenue, drinkRevenue, hasFoodDrink,
    covers, avgParty, avgCheck, checkCount,
    completionRate, noShowRate, walkOutRate,
    avgBookingWindow,
    pickup    = { count: 0, totalRevenue: 0, totalTips: 0, hostCount: 0, hostRevenue: 0, widgetCount: 0, widgetRevenue: 0, byMethod: {}, itemMap: {} },
    giftCards: gcData = { count: 0, totalSold: 0, totalRedeemed: 0, outstanding: 0 },
    dailyChart = [],
    currentDays = 7,
  } = data;

  const combinedRevenue  = totalRevenue + (pickup?.totalRevenue ?? 0);
  const combinedTips     = (paid.reduce((s, r) => s + (r.tip || 0), 0)) + (pickup?.totalTips ?? 0);
  const hasPickup        = (pickup?.count ?? 0) > 0;
  const hasGiftCards     = (gcData?.count ?? 0) > 0;
  const isToday          = currentDays === 0;

  const todayStr = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  return (
    <div className="space-y-5">

      {/* ── Today at a Glance banner ──────────────────────────────────────── */}
      {isToday && (
        <>
          <div className="bg-neutral-900 rounded-2xl p-5 text-white">
            <p className="text-xs font-semibold uppercase tracking-widest text-neutral-400 mb-1">{todayStr}</p>
            <p className="text-4xl font-bold leading-tight mb-0.5">{fmt$(combinedRevenue)}</p>
            <p className="text-sm text-neutral-400">
              {checkCount + (pickup?.count ?? 0)} transaction{(checkCount + (pickup?.count ?? 0)) !== 1 ? 's' : ''}
              {combinedTips > 0 ? ` · ${fmt$(combinedTips)} tips` : ''}
            </p>
            {(totalRevenue > 0 || (pickup?.totalRevenue ?? 0) > 0) && (
              <div className="flex gap-4 mt-3 pt-3 border-t border-neutral-700">
                <div>
                  <p className="text-xs text-neutral-500">Dine-in</p>
                  <p className="text-base font-semibold">{fmt$(totalRevenue)}</p>
                </div>
                {(pickup?.totalRevenue ?? 0) > 0 && (
                  <div>
                    <p className="text-xs text-neutral-500">Pickup</p>
                    <p className="text-base font-semibold">{fmt$(pickup.totalRevenue)}</p>
                  </div>
                )}
                {hasFoodDrink && (
                  <>
                    <div>
                      <p className="text-xs text-neutral-500">Food</p>
                      <p className="text-base font-semibold">{fmt$(foodRevenue)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Drinks</p>
                      <p className="text-base font-semibold">{fmt$(drinkRevenue)}</p>
                    </div>
                  </>
                )}
              </div>
            )}
            {combinedRevenue === 0 && (
              <p className="text-xs text-neutral-500 mt-2 italic">No paid transactions yet today</p>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <MetricCard
              title="Covers"
              value={covers.toLocaleString()}
              sub={`${paid.length} res`}
              dim={covers === 0}
            />
            <MetricCard
              title="Avg Check"
              value={fmt$(avgCheck)}
              sub="dine-in"
              dim={avgCheck == null}
            />
            <MetricCard
              title="Tips"
              value={fmt$(combinedTips || null)}
              sub="earned today"
              dim={combinedTips === 0}
            />
          </div>
        </>
      )}

      {/* ── Multi-period comparison charts ─────────────────────────────── */}
      {(mergedRevChart.length > 0) && (
        <>
          <ChartCard title="Revenue Over Time">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={mergedRevChart} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="_x" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={fmtAxis} width={44} />
                <Tooltip content={<ChartTooltipContent formatter={v => v != null ? `$${v.toFixed(2)}` : '—'} />} />
                {allSeries.length > 1 && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />}
                {allSeries.map((s, i) => (
                  <Line
                    key={s.label + '_total'}
                    type="monotone"
                    dataKey={s.label + '_total'}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={i === 0 ? 2.5 : 1.8}
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Tips Over Time">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={mergedRevChart} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="_x" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={fmtAxis} width={44} />
                <Tooltip content={<ChartTooltipContent formatter={v => v != null ? `$${v.toFixed(2)}` : '—'} />} />
                {allSeries.length > 1 && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />}
                {allSeries.map((s, i) => (
                  <Bar
                    key={s.label + '_tips'}
                    dataKey={s.label + '_tips'}
                    name={s.label}
                    fill={s.color}
                    opacity={i === 0 ? 0.9 : 0.65}
                    radius={[2, 2, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Avg Check Size Over Time">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={mergedRevChart} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="_x" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} width={44} />
                <Tooltip content={<ChartTooltipContent formatter={v => v != null ? `$${v.toFixed(2)}` : '—'} />} />
                {allSeries.length > 1 && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />}
                {allSeries.map((s, i) => (
                  <Line
                    key={s.label + '_avg'}
                    type="monotone"
                    dataKey={s.label + '_avg'}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={i === 0 ? 2 : 1.5}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}

      {/* Summary cards — skip on Today since the banner already shows revenue */}
      {!isToday && (
        <>
          <SectionLabel>Revenue</SectionLabel>
          {comparePeriods.length > 0 ? (
            <CmpTable
              primary={data}
              comparePeriods={comparePeriods}
              primaryLabel={primaryLabel}
              metrics={[
                { label: 'Total Revenue',  fn: d => fmt$((d.totalRevenue||0)+(d.pickup?.totalRevenue??0)) },
                { label: 'Avg Check Size', fn: d => fmt$(d.avgCheck) },
                ...(hasFoodDrink ? [
                  { label: 'Food Revenue',  fn: d => (d.foodRevenue||0)>0 ? fmt$(d.foodRevenue) : '—' },
                  { label: 'Drink Revenue', fn: d => (d.drinkRevenue||0)>0 ? fmt$(d.drinkRevenue) : '—' },
                ] : []),
              ]}
            />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <MetricCard
                title="Total Revenue"
                value={fmt$(combinedRevenue)}
                sub={pickup.count > 0
                  ? `Dine-in ${fmt$(totalRevenue)} · Pickup ${fmt$(pickup.totalRevenue)}`
                  : `${checkCount} paid check${checkCount !== 1 ? 's' : ''}`}
                dim={combinedRevenue === 0}
              />
              <MetricCard
                title="Avg Check Size"
                value={fmt$(avgCheck)}
                sub="dine-in, pre-tip, per reservation"
                dim={avgCheck == null}
              />
              {hasFoodDrink && (
                <>
                  <MetricCard title="Food Revenue" value={fmt$(foodRevenue)} sub="food items subtotal" />
                  <MetricCard title="Drink Revenue" value={fmt$(drinkRevenue)} sub="drink items subtotal" />
                </>
              )}
            </div>
          )}
        </>
      )}

      <SectionLabel>Revenue by Channel</SectionLabel>
      {comparePeriods.length > 0 ? (
        <CmpTable
          primary={data}
          comparePeriods={comparePeriods}
          primaryLabel={primaryLabel}
          metrics={[
            { label: 'Dine-in',         fn: d => `${fmt$(d.totalRevenue||0)} (${d.checkCount??0} checks)` },
            { label: 'Pickup – Staff',  fn: d => (d.pickup?.hostRevenue??0)>0 ? `${fmt$(d.pickup.hostRevenue)} (${d.pickup.hostCount??0})` : '—' },
            { label: 'Pickup – Online', fn: d => (d.pickup?.widgetRevenue??0)>0 ? `${fmt$(d.pickup.widgetRevenue)} (${d.pickup.widgetCount??0})` : '—' },
          ]}
        />
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
          <BarRow
            label="Dine-in"
            value={totalRevenue}
            max={combinedRevenue || 1}
            valueStr={`${fmt$(totalRevenue)}  (${checkCount} checks)`}
          />
          {pickup.hostRevenue > 0 && (
            <BarRow
              label="Pickup – Staff"
              value={pickup.hostRevenue}
              max={combinedRevenue || 1}
              valueStr={`${fmt$(pickup.hostRevenue)}  (${pickup.hostCount} orders)`}
            />
          )}
          {pickup.widgetRevenue > 0 && (
            <BarRow
              label="Pickup – Online"
              value={pickup.widgetRevenue}
              max={combinedRevenue || 1}
              valueStr={`${fmt$(pickup.widgetRevenue)}  (${pickup.widgetCount} orders)`}
            />
          )}
          {!hasPickup && (
            <p className="text-xs text-neutral-400 italic">No paid pickup orders in this period</p>
          )}
        </div>
      )}

      {hasPickup && Object.keys(pickup.byMethod).length > 0 && (
        <>
          <SectionLabel>Pickup Payment Methods</SectionLabel>
          <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
            {Object.entries(pickup.byMethod).map(([label, m]) => (
              <BarRow
                key={label}
                label={label}
                value={m.revenue}
                max={pickup.totalRevenue || 1}
                valueStr={`${fmt$(m.revenue)}  (${m.count}×)`}
              />
            ))}
          </div>
        </>
      )}

      {hasGiftCards && (
        <>
          <SectionLabel>Gift Card Sales — Deferred Revenue</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            <MetricCard
              title="Cards Sold"
              value={gcData.count.toString()}
              sub={`${fmt$(gcData.totalSold)} total value`}
            />
            <MetricCard
              title="Outstanding Liability"
              value={fmt$(gcData.outstanding)}
              sub={`${fmt$(gcData.totalRedeemed)} redeemed`}
            />
          </div>
        </>
      )}

      <SectionLabel>Tips</SectionLabel>
      {comparePeriods.length > 0 ? (
        <CmpTable
          primary={data}
          comparePeriods={comparePeriods}
          primaryLabel={primaryLabel}
          metrics={[
            { label: 'Tips Collected', fn: d => (d.totalTips??0)>0 ? fmt$(d.totalTips) : '—' },
            { label: 'Avg Tip %',      fn: d => fmtPct(d.tipPct) },
          ]}
        />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <MetricCard
            title="Tips Collected"
            value={tipsHaveData ? fmt$(totalTips) : '—'}
            sub={tipsHaveData ? `across ${paid.length} paid check${paid.length !== 1 ? 's' : ''}` : 'No tip data yet'}
            dim={!tipsHaveData}
          />
          <MetricCard
            title="Avg Tip %"
            value={fmtPct(tipPct)}
            sub={tipSampleSize > 0 ? `from ${tipSampleSize} check${tipSampleSize !== 1 ? 's' : ''}` : 'No data yet'}
            dim={tipPct == null}
          />
        </div>
      )}

      <SectionLabel>Guest Experience</SectionLabel>
      {comparePeriods.length > 0 ? (
        <CmpTable
          primary={data}
          comparePeriods={comparePeriods}
          primaryLabel={primaryLabel}
          metrics={[
            { label: 'Total Covers',       fn: d => (d.covers??0).toLocaleString() },
            { label: 'Avg Party Size',     fn: d => d.avgParty!=null ? d.avgParty.toFixed(1) : '—' },
            { label: 'Avg Turn Time',      fn: d => fmtMins(d.turnMins) },
            { label: 'Avg Booking Window', fn: d => d.avgBookingWindow!=null ? `${d.avgBookingWindow.toFixed(0)}d` : '—' },
          ]}
        />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <MetricCard
            title="Total Covers"
            value={covers.toLocaleString()}
            sub={`${paid.length} paid reservation${paid.length !== 1 ? 's' : ''}`}
          />
          <MetricCard
            title="Avg Party Size"
            value={avgParty != null ? avgParty.toFixed(1) : '—'}
            sub="guests per reservation"
            dim={avgParty == null}
          />
          <MetricCard
            title="Avg Turn Time"
            value={fmtMins(turnMins)}
            sub={turnSampleSize > 0 ? `from ${turnSampleSize} reservation${turnSampleSize !== 1 ? 's' : ''}` : 'Requires paidAt data'}
            dim={turnMins == null}
          />
          <MetricCard
            title="Avg Booking Window"
            value={avgBookingWindow != null ? `${avgBookingWindow.toFixed(0)}d` : '—'}
            sub="days booked in advance"
            dim={avgBookingWindow == null}
          />
        </div>
      )}

      <SectionLabel>Outcomes</SectionLabel>
      {comparePeriods.length > 0 ? (
        <CmpTable
          primary={data}
          comparePeriods={comparePeriods}
          primaryLabel={primaryLabel}
          metrics={[
            { label: 'Completion Rate', fn: d => fmtPct(d.completionRate,0) },
            { label: 'No-Show Rate',    fn: d => fmtPct(d.noShowRate,0) },
            { label: 'Walk-Out Rate',   fn: d => fmtPct(d.walkOutRate,0) },
          ]}
        />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <MetricCard
            title="Completion Rate"
            value={fmtPct(completionRate, 0)}
            sub={`${paid.length} paid of ${outcomes.length} total`}
            dim={completionRate == null}
          />
          <MetricCard
            title="No-Show Rate"
            value={fmtPct(noShowRate, 0)}
            sub={`${data.noShows.length} no-show${data.noShows.length !== 1 ? 's' : ''}`}
            dim={noShowRate == null}
          />
          <MetricCard
            title="Walk-Out Rate"
            value={fmtPct(walkOutRate, 0)}
            sub={`${data.walkOuts.length} walk-out${data.walkOuts.length !== 1 ? 's' : ''}`}
            dim={walkOutRate == null}
          />
        </div>
      )}
    </div>
  );
};

const MenuTab = ({ data, comparePeriods = [], primaryLabel = 'Primary' }) => {
  const { menuItems, compedItems, foodRevenue, drinkRevenue, hasFoodDrink } = data;
  const pickup = data.pickup ?? { itemMap: {} };
  const [sortBy, setSortBy] = useState('revenue'); // 'revenue' | 'count'

  // Merge dine-in items with pickup items
  const mergedMap = {};
  for (const item of menuItems) {
    mergedMap[item.name] = { ...item };
  }
  for (const [name, pItem] of Object.entries(pickup.itemMap)) {
    if (mergedMap[name]) {
      mergedMap[name].revenue += pItem.revenue;
      mergedMap[name].count   += pItem.count;
    } else {
      mergedMap[name] = { name, revenue: pItem.revenue, count: pItem.count, isDrink: false };
    }
  }
  const merged = Object.values(mergedMap).sort((a, b) => b[sortBy] - a[sortBy]);
  const maxVal = merged.length ? merged[0][sortBy] : 1;
  const hasPickupItems = Object.keys(pickup.itemMap).length > 0;

  return (
    <div className="space-y-5">
      {/* ── Menu comparison ─────────────────────────────────────────────── */}
      {comparePeriods.length > 0 && (
        <>
          <SectionLabel>Top Items — Period Comparison</SectionLabel>
          <ChartCard title="Revenue by Item">
            {(() => {
              const allNames = [...new Set([
                ...(data.menuItems || []).slice(0, 8).map(i => i.name),
                ...comparePeriods.flatMap(cp => (cp.stats?.menuItems || []).slice(0, 5).map(i => i.name)),
              ])].slice(0, 10);
              const chartData = allNames.map(name => {
                const shortName = name.length > 16 ? name.slice(0, 16) + '…' : name;
                const obj = { name: shortName };
                obj[primaryLabel] = Math.round(((data.menuItems || []).find(i => i.name === name)?.revenue ?? 0) * 100) / 100;
                comparePeriods.forEach(cp => {
                  obj[cp.label] = Math.round(((cp.stats?.menuItems || []).find(i => i.name === name)?.revenue ?? 0) * 100) / 100;
                });
                return obj;
              }).sort((a, b) => (b[primaryLabel] || 0) - (a[primaryLabel] || 0));
              return (
                <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 38)}>
                  <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={fmtAxis} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#374151' }} tickLine={false} axisLine={false} width={110} />
                    <Tooltip content={<ChartTooltipContent formatter={v => `$${v.toFixed(2)}`} />} />
                    <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                    <Bar dataKey={primaryLabel} fill={COMPARE_PALETTE[0]} radius={[0, 3, 3, 0]} />
                    {comparePeriods.map(cp => (
                      <Bar key={cp.key} dataKey={cp.label} fill={cp.color} radius={[0, 3, 3, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </ChartCard>

          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-neutral-100 bg-neutral-50">
                    <th className="px-4 py-2.5 text-left font-semibold text-neutral-500">Item</th>
                    <th className="px-4 py-2.5 text-right font-bold" style={{ color: COMPARE_PALETTE[0] }}>{primaryLabel}</th>
                    {comparePeriods.map(cp => (
                      <th key={cp.key} className="px-4 py-2.5 text-right font-bold" style={{ color: cp.color }}>{cp.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...new Set([
                    ...(data.menuItems || []).slice(0, 10).map(i => i.name),
                    ...comparePeriods.flatMap(cp => (cp.stats?.menuItems || []).slice(0, 8).map(i => i.name)),
                  ])].slice(0, 12).map(name => (
                    <tr key={name} className="border-b border-neutral-50 hover:bg-neutral-50">
                      <td className="px-4 py-2 font-medium text-neutral-600 max-w-[120px] truncate">{name}</td>
                      <td className="px-4 py-2 text-right font-semibold text-neutral-900">
                        {fmt$((data.menuItems || []).find(i => i.name === name)?.revenue ?? null)}
                        <span className="text-neutral-400 ml-1">({(data.menuItems || []).find(i => i.name === name)?.count ?? 0}×)</span>
                      </td>
                      {comparePeriods.map(cp => {
                        const item = (cp.stats?.menuItems || []).find(i => i.name === name);
                        return (
                          <td key={cp.key} className="px-4 py-2 text-right text-neutral-600">
                            {item ? <>{fmt$(item.revenue)} <span className="text-neutral-400">({item.count}×)</span></> : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {hasFoodDrink && (
        <>
          <SectionLabel>Category Split (Dine-in)</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            <MetricCard title="Food Revenue" value={fmt$(foodRevenue)} sub="food items" />
            <MetricCard title="Drink Revenue" value={fmt$(drinkRevenue)} sub="drink items" />
          </div>
        </>
      )}

      {/* Food vs. Drink chart */}
      {hasFoodDrink && (
        <ChartCard title="Food vs. Drink Revenue">
          <ResponsiveContainer width="100%" height={80}>
            <BarChart
              data={[{ name: 'Revenue', Food: Math.round(foodRevenue * 100) / 100, Drink: Math.round(drinkRevenue * 100) / 100 }]}
              layout="vertical"
              margin={{ top: 0, right: 40, bottom: 0, left: 0 }}
            >
              <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={fmtAxis} />
              <YAxis type="category" dataKey="name" hide />
              <Tooltip content={<ChartTooltipContent formatter={v => `$${v.toFixed(2)}`} />} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Bar dataKey="Food" fill={CHART_COLORS.food} stackId="a" />
              <Bar dataKey="Drink" fill={CHART_COLORS.drink} stackId="a" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Top items chart */}
      {merged.length > 0 && (
        <ChartCard
          title="Top Items"
          action={
            <ChartToggle
              options={[{ label: 'Revenue', value: 'revenue' }, { label: 'Count', value: 'count' }]}
              value={sortBy}
              onChange={setSortBy}
            />
          }
        >
          <ResponsiveContainer width="100%" height={Math.max(160, Math.min(merged.slice(0, 10).length * 32, 320))}>
            <BarChart
              data={[...merged].sort((a, b) => sortBy === 'revenue' ? b.revenue - a.revenue : b.count - a.count)
                .slice(0, 10)
                .map(i => ({ name: i.name.length > 20 ? i.name.slice(0, 20) + '…' : i.name, revenue: Math.round(i.revenue * 100) / 100, count: i.count }))}
              layout="vertical"
              margin={{ top: 0, right: 8, bottom: 0, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={sortBy === 'revenue' ? fmtAxis : undefined} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#374151' }} tickLine={false} axisLine={false} width={115} />
              <Tooltip content={<ChartTooltipContent formatter={(v, k) => k === 'revenue' ? `$${v.toFixed(2)}` : `${v} sold`} />} />
              <Bar dataKey={sortBy} name={sortBy === 'revenue' ? 'Revenue' : 'Units Sold'} fill={CHART_COLORS.revenue} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <SectionLabel>
        Top Items —{' '}
        <span
          className="cursor-pointer underline text-neutral-500"
          onClick={() => setSortBy(sortBy === 'revenue' ? 'count' : 'revenue')}
        >
          by {sortBy === 'revenue' ? 'Revenue' : 'Orders'} (tap to toggle)
        </span>
        {hasPickupItems && <span className="ml-2 text-neutral-400 not-italic font-normal">· includes pickup</span>}
      </SectionLabel>
      {merged.length === 0 ? (
        <EmptyState msg="No item data yet — items are captured at payment time." />
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
          {merged.slice(0, 15).map((item) => (
            <BarRow
              key={item.name}
              label={item.name}
              value={item[sortBy]}
              max={maxVal}
              valueStr={sortBy === 'revenue' ? fmt$(item.revenue) : `${item.count}x`}
            />
          ))}
        </div>
      )}

      {compedItems.length > 0 && (
        <>
          <SectionLabel>Comped Items ({compedItems.length})</SectionLabel>
          <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-1">
            {compedItems.slice(0, 10).map((ci, i) => (
              <div key={i} className="flex justify-between text-xs text-neutral-600">
                <span>{ci.name}</span>
                <span className="text-neutral-400">{fmt$(ci.price)}</span>
              </div>
            ))}
            {compedItems.length > 10 && (
              <p className="text-xs text-neutral-400 pt-1">+{compedItems.length - 10} more</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ServersTab = ({ data, comparePeriods = [], primaryLabel = 'Primary' }) => {
  const { servers } = data;

  if (servers.length === 0) return <EmptyState msg="No server assignment data yet." />;

  const maxRevenue = servers[0]?.revenue || 1;

  return (
    <div className="space-y-5">

      {/* ── Server comparison ───────────────────────────────────────────── */}
      {comparePeriods.length > 0 && (
        <>
          <SectionLabel>Server Revenue — Period Comparison</SectionLabel>
          {(() => {
            const allNames = [...new Set([
              ...(data.servers || []).map(s => s.name),
              ...comparePeriods.flatMap(cp => (cp.stats?.servers || []).map(s => s.name)),
            ])];
            const chartData = allNames.map(name => {
              const shortName = (name || 'Unassigned').split(' ')[0];
              const obj = { name: shortName };
              obj[primaryLabel] = Math.round(((data.servers || []).find(s => s.name === name)?.revenue ?? 0) * 100) / 100;
              comparePeriods.forEach(cp => {
                obj[cp.label] = Math.round(((cp.stats?.servers || []).find(s => s.name === name)?.revenue ?? 0) * 100) / 100;
              });
              return obj;
            }).sort((a, b) => (b[primaryLabel] || 0) - (a[primaryLabel] || 0));
            return (
              <ChartCard title="Revenue by Server">
                <ResponsiveContainer width="100%" height={Math.max(120, chartData.length * 44)}>
                  <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={fmtAxis} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#374151' }} tickLine={false} axisLine={false} width={72} />
                    <Tooltip content={<ChartTooltipContent formatter={v => `$${v.toFixed(2)}`} />} />
                    <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                    <Bar dataKey={primaryLabel} fill={COMPARE_PALETTE[0]} radius={[0, 3, 3, 0]} />
                    {comparePeriods.map(cp => (
                      <Bar key={cp.key} dataKey={cp.label} fill={cp.color} radius={[0, 3, 3, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            );
          })()}

          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-neutral-100 bg-neutral-50">
                    <th className="px-4 py-2.5 text-left font-semibold text-neutral-500">Server</th>
                    <th className="px-4 py-2.5 text-right font-bold" style={{ color: COMPARE_PALETTE[0] }}>{primaryLabel}</th>
                    {comparePeriods.map(cp => (
                      <th key={cp.key} className="px-4 py-2.5 text-right font-bold" style={{ color: cp.color }}>{cp.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...new Set([
                    ...(data.servers || []).map(s => s.name),
                    ...comparePeriods.flatMap(cp => (cp.stats?.servers || []).map(s => s.name)),
                  ])].map(name => {
                    const primary = (data.servers || []).find(s => s.name === name);
                    return (
                      <tr key={name} className="border-b border-neutral-50 hover:bg-neutral-50">
                        <td className="px-4 py-2 font-medium text-neutral-600">{name || 'Unassigned'}</td>
                        <td className="px-4 py-2 text-right font-semibold text-neutral-900">
                          {primary ? <>{fmt$(primary.revenue)} <span className="text-neutral-400">({fmtPct(primary.tipPct)} tip)</span></> : '—'}
                        </td>
                        {comparePeriods.map(cp => {
                          const s = (cp.stats?.servers || []).find(sv => sv.name === name);
                          return (
                            <td key={cp.key} className="px-4 py-2 text-right text-neutral-600">
                              {s ? <>{fmt$(s.revenue)} <span className="text-neutral-400">({fmtPct(s.tipPct)} tip)</span></> : '—'}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Server revenue & tips chart */}
      {servers.length > 1 && (
        <ChartCard title="Revenue & Tips by Server">
          <ResponsiveContainer width="100%" height={Math.max(120, Math.min(servers.length * 44, 300))}>
            <BarChart
              data={servers.map(s => ({
                name: (s.name || 'Unassigned').split(' ')[0],
                Revenue: Math.round(s.revenue * 100) / 100,
                Tips: Math.round((s.tips || 0) * 100) / 100,
              }))}
              layout="vertical"
              margin={{ top: 0, right: 8, bottom: 0, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={fmtAxis} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#374151' }} tickLine={false} axisLine={false} width={72} />
              <Tooltip content={<ChartTooltipContent formatter={v => `$${v.toFixed(2)}`} />} />
              <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
              <Bar dataKey="Revenue" fill={CHART_COLORS.revenue} radius={[0, 3, 3, 0]} />
              <Bar dataKey="Tips" fill={CHART_COLORS.tips} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <SectionLabel>Server Performance</SectionLabel>

      {/* Mobile-friendly cards */}
      <div className="space-y-3">
        {servers.map((s) => (
          <div key={s.name} className="bg-white border border-neutral-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-neutral-800 truncate">{s.name}</p>
              <p className="text-sm font-bold text-neutral-900 shrink-0 ml-2">{fmt$(s.revenue)}</p>
            </div>
            <div className="w-full bg-neutral-100 rounded-full h-1.5 mb-3">
              <div
                className="bg-neutral-800 h-1.5 rounded-full"
                style={{ width: `${Math.min((s.revenue / maxRevenue) * 100, 100)}%` }}
              />
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <p className="text-xs text-neutral-400">Covers</p>
                <p className="text-sm font-semibold text-neutral-700">{s.covers}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400">Avg Check</p>
                <p className="text-sm font-semibold text-neutral-700">{fmt$(s.avgCheck)}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400">Tips $</p>
                <p className="text-sm font-semibold text-neutral-700">{s.tips > 0 ? fmt$(s.tips) : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400">Tip %</p>
                <p className="text-sm font-semibold text-neutral-700">{fmtPct(s.tipPct)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PatternsTab = ({ data, comparePeriods = [], primaryLabel = 'Primary' }) => {
  const { dowCounts, dowRevenue, periodMap, windowBuckets } = data;
  const [dowMetric,    setDowMetric]    = useState('count');
  const [periodMetric, setPeriodMetric] = useState('count');

  const dowData = DOW_LABELS.map((d, i) => {
    const obj = { day: d };
    obj[primaryLabel] = dowMetric === 'count' ? (dowCounts[i] || 0) : Math.round((dowRevenue[i] || 0) * 100) / 100;
    comparePeriods.forEach(cp => {
      obj[cp.label] = dowMetric === 'count'
        ? (cp.stats?.dowCounts?.[i] || 0)
        : Math.round((cp.stats?.dowRevenue?.[i] || 0) * 100) / 100;
    });
    return obj;
  });

  const periods = MEAL_PERIODS.map(p => {
    const obj = { label: p.label };
    obj[primaryLabel] = periodMetric === 'count' ? (periodMap[p.label]?.count || 0) : Math.round((periodMap[p.label]?.revenue || 0) * 100) / 100;
    comparePeriods.forEach(cp => {
      obj[cp.label] = periodMetric === 'count'
        ? (cp.stats?.periodMap?.[p.label]?.count || 0)
        : Math.round((cp.stats?.periodMap?.[p.label]?.revenue || 0) * 100) / 100;
    });
    return obj;
  });

  const bookingData = windowBuckets.map(b => {
    const obj = { label: b.label };
    obj[primaryLabel] = b.count;
    comparePeriods.forEach(cp => {
      obj[cp.label] = (cp.stats?.windowBuckets || []).find(wb => wb.label === b.label)?.count || 0;
    });
    return obj;
  });

  return (
    <div className="space-y-5">
      <ChartCard
        title="Busiest Days of Week"
        action={<ChartToggle options={[{ label: 'Covers', value: 'count' }, { label: 'Revenue', value: 'revenue' }]} value={dowMetric} onChange={setDowMetric} />}
      >
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={dowData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={dowMetric === 'revenue' ? fmtAxis : undefined} width={36} />
            <Tooltip content={<ChartTooltipContent formatter={(v) => dowMetric === 'revenue' ? `$${v.toFixed(2)}` : `${v} res`} />} />
            {comparePeriods.length > 0 && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />}
            <Bar dataKey={primaryLabel} name={primaryLabel} fill={COMPARE_PALETTE[0]} radius={[3, 3, 0, 0]} />
            {comparePeriods.map(cp => (
              <Bar key={cp.key} dataKey={cp.label} name={cp.label} fill={cp.color} radius={[3, 3, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="By Meal Period"
        action={<ChartToggle options={[{ label: 'Covers', value: 'count' }, { label: 'Revenue', value: 'revenue' }]} value={periodMetric} onChange={setPeriodMetric} />}
      >
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={periods} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={periodMetric === 'revenue' ? fmtAxis : undefined} width={36} />
            <Tooltip content={<ChartTooltipContent formatter={(v) => periodMetric === 'revenue' ? `$${v.toFixed(2)}` : `${v} res`} />} />
            {comparePeriods.length > 0 && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />}
            <Bar dataKey={primaryLabel} name={primaryLabel} fill={COMPARE_PALETTE[3]} radius={[3, 3, 0, 0]} />
            {comparePeriods.map(cp => (
              <Bar key={cp.key} dataKey={cp.label} name={cp.label} fill={cp.color} radius={[3, 3, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Booking Window Distribution">
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={bookingData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6b7280' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} width={28} />
            <Tooltip content={<ChartTooltipContent formatter={v => `${v} res`} />} />
            {comparePeriods.length > 0 && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />}
            <Bar dataKey={primaryLabel} name={primaryLabel} fill={COMPARE_PALETTE[2]} radius={[3, 3, 0, 0]} />
            {comparePeriods.map(cp => (
              <Bar key={cp.key} dataKey={cp.label} name={cp.label} fill={cp.color} radius={[3, 3, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        <p className="text-xs text-neutral-400 mt-3 px-0.5">
          Requires a <span className="font-mono">requestDate</span> field on reservations. Historical reservations without it are excluded.
        </p>
      </ChartCard>
    </div>
  );
};

// Build dailyChart and monthlyChart from raw Firestore data for any period
function buildPeriodCharts(reservations, pickupOrders) {
  const dailyMap = {};
  const monthlyMap = {};
  const ensureDay = (ds) => {
    if (!dailyMap[ds]) dailyMap[ds] = { dineIn: 0, pickup: 0, tips: 0, covers: 0, checkCount: 0 };
  };
  const ensureMonth = (ms) => {
    if (!monthlyMap[ms]) monthlyMap[ms] = { dineIn: 0, pickup: 0, tips: 0, covers: 0, checkCount: 0 };
  };
  for (const r of (reservations || []).map(normalizeReservation)) {
    if (!PAID_STATUSES.has(r.status)) continue;
    const ds = r.dateOnly || (r.date?.toDate ? r.date.toDate().toISOString().slice(0, 10) : null);
    if (!ds) continue;
    const ms = ds.slice(0, 7); // 'YYYY-MM'
    ensureDay(ds);
    ensureMonth(ms);
    if ((r.total || 0) > 0) {
      dailyMap[ds].dineIn     += r.total;
      dailyMap[ds].checkCount += 1;
      monthlyMap[ms].dineIn     += r.total;
      monthlyMap[ms].checkCount += 1;
    }
    dailyMap[ds].tips   += r.tip || 0;
    dailyMap[ds].covers += Number(r.partySize) || 0;
    monthlyMap[ms].tips   += r.tip || 0;
    monthlyMap[ms].covers += Number(r.partySize) || 0;
  }
  for (const o of (pickupOrders || [])) {
    if (o.paymentStatus !== 'paid') continue;
    const ts = o.createdAt;
    const ds = ts != null ? new Date(typeof ts === 'number' ? ts : ts.toMillis()).toISOString().slice(0, 10) : null;
    if (!ds) continue;
    const ms = ds.slice(0, 7);
    ensureDay(ds);
    ensureMonth(ms);
    const rev = o.payment?.totalCaptured ?? o.total ?? 0;
    const tip = o.payment?.tipAmount ?? 0;
    dailyMap[ds].pickup += rev;
    dailyMap[ds].tips   += tip;
    monthlyMap[ms].pickup += rev;
    monthlyMap[ms].tips   += tip;
  }

  const dailyChart = Object.entries(dailyMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateStr, v]) => {
      const d = new Date(dateStr + 'T12:00:00');
      return {
        dateStr,
        day: d.getDate(),
        label: `${d.getMonth() + 1}/${d.getDate()}`,
        dineIn:   Math.round(v.dineIn   * 100) / 100,
        pickup:   Math.round(v.pickup   * 100) / 100,
        total:    Math.round((v.dineIn + v.pickup) * 100) / 100,
        tips:     Math.round(v.tips     * 100) / 100,
        covers:   v.covers,
        avgCheck: v.checkCount > 0 ? Math.round((v.dineIn / v.checkCount) * 100) / 100 : 0,
      };
    });

  const monthlyChart = Object.entries(monthlyMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([ms, v]) => {
      const [, m] = ms.split('-').map(Number);
      return {
        monthKey: ms,
        monthIdx: m - 1,
        label: MONTH_SHORT[m - 1],
        total:    Math.round((v.dineIn + v.pickup) * 100) / 100,
        tips:     Math.round(v.tips     * 100) / 100,
        covers:   v.covers,
        avgCheck: v.checkCount > 0 ? Math.round((v.dineIn / v.checkCount) * 100) / 100 : 0,
      };
    });

  return { dailyChart, monthlyChart };
}

// ── Waitlist analytics helpers ─────────────────────────────────────────────────

function computeWaitlistMetrics(docs) {
  if (!docs.length) return null;

  const total    = docs.length;
  const waitMins = docs.map(d => d.waitMinutes || 0);
  const avgWait  = Math.round(waitMins.reduce((s, v) => s + v, 0) / total);
  const maxWait  = Math.max(...waitMins);
  const minWait  = Math.min(...waitMins);
  const avgPartySize = docs.reduce((s, d) => s + (d.partySize || 0), 0) / total;
  const kioskCount   = docs.filter(d => d.addedByKiosk).length;
  const kioskPct     = Math.round((kioskCount / total) * 100);

  const quoted    = docs.filter(d => d.quotedAccuracy != null);
  const avgAccuracy = quoted.length > 0
    ? Math.round(quoted.reduce((s, d) => s + d.quotedAccuracy, 0) / quoted.length)
    : null;

  // Daily trend
  const byDate = {};
  docs.forEach(d => {
    if (!d.date) return;
    if (!byDate[d.date]) byDate[d.date] = [];
    byDate[d.date].push(d.waitMinutes || 0);
  });
  const dailyTrend = Object.keys(byDate).sort().map(date => ({
    date: date.slice(5),
    avg:  Math.round(byDate[date].reduce((s, v) => s + v, 0) / byDate[date].length),
    count: byDate[date].length,
  }));

  // Day of week
  const byDow = Array.from({ length: 7 }, () => []);
  docs.forEach(d => { if (d.seatedAt) byDow[new Date(d.seatedAt).getDay()].push(d.waitMinutes || 0); });
  const dowChart = DOW_LABELS.map((day, i) => ({
    day,
    avg:   byDow[i].length ? Math.round(byDow[i].reduce((s, v) => s + v, 0) / byDow[i].length) : 0,
    count: byDow[i].length,
  }));

  // Hour of day (only hours with data)
  const byHour = Array.from({ length: 24 }, () => []);
  docs.forEach(d => { if (d.hour != null) byHour[d.hour].push(d.waitMinutes || 0); });
  const hourChart = byHour
    .map((arr, h) => ({
      hour:  h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`,
      avg:   arr.length ? Math.round(arr.reduce((s, v) => s + v, 0) / arr.length) : 0,
      count: arr.length,
    }))
    .filter(h => h.count > 0);

  // Party size buckets
  const sizeDefs = [
    { label: '1',   test: (ps) => ps === 1 },
    { label: '2',   test: (ps) => ps === 2 },
    { label: '3–4', test: (ps) => ps >= 3 && ps <= 4 },
    { label: '5–6', test: (ps) => ps >= 5 && ps <= 6 },
    { label: '7+',  test: (ps) => ps >= 7 },
  ];
  const partySizeChart = sizeDefs.map(def => ({
    label: def.label,
    count: docs.filter(d => def.test(d.partySize || 0)).length,
    avgWait: (() => {
      const sub = docs.filter(d => def.test(d.partySize || 0));
      return sub.length ? Math.round(sub.reduce((s, d) => s + (d.waitMinutes || 0), 0) / sub.length) : 0;
    })(),
  }));

  // Quote accuracy buckets
  const accDefs = [
    { label: '> +10m late',     test: (v) => v > 10 },
    { label: '+6 to +10m',      test: (v) => v > 5 && v <= 10 },
    { label: '+1 to +5m',       test: (v) => v >= 1 && v <= 5 },
    { label: 'On time (±0)',    test: (v) => v === 0 },
    { label: '-1 to -5m early', test: (v) => v >= -5 && v < 0 },
    { label: '-6 to -10m early',test: (v) => v >= -10 && v < -5 },
    { label: '> -10m early',    test: (v) => v < -10 },
  ];
  const accuracyBuckets = accDefs.map(def => ({
    label: def.label,
    count: quoted.filter(d => def.test(d.quotedAccuracy)).length,
  }));

  return {
    total, avgWait, maxWait, minWait, avgPartySize, kioskPct, avgAccuracy,
    dailyTrend, dowChart, hourChart, partySizeChart, accuracyBuckets,
    quotedCount: quoted.length,
  };
}

// ── WaitlistTab ────────────────────────────────────────────────────────────────

const WaitlistTab = ({ restaurantId, startMs, endMs }) => {
  const [wlLoading, setWlLoading] = React.useState(true);
  const [wlMetrics, setWlMetrics] = React.useState(null);

  React.useEffect(() => {
    if (!restaurantId || !startMs || !endMs) return;
    setWlLoading(true);
    getDocs(
      query(
        collection(db, 'restaurants_data', restaurantId, 'waitlist_metrics'),
        where('seatedAt', '>=', startMs),
        where('seatedAt', '<=', endMs),
      )
    )
      .then(snap => setWlMetrics(computeWaitlistMetrics(snap.docs.map(d => d.data()))))
      .catch(e => console.error('[WaitlistTab]', e))
      .finally(() => setWlLoading(false));
  }, [restaurantId, startMs, endMs]);

  if (wlLoading) return <p className="text-sm text-neutral-400 text-center py-12">Loading waitlist data…</p>;
  if (!wlMetrics) return (
    <div className="mt-4 text-center py-12">
      <p className="text-sm text-neutral-400 italic">No completed waitlist seatings in this period.</p>
      <p className="text-xs text-neutral-300 mt-1">Data is recorded automatically each time a waitlist guest is seated from the POS floor map.</p>
    </div>
  );

  const m = wlMetrics;

  return (
    <div className="flex flex-col gap-4 mt-2">

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <MetricCard title="Avg Wait Time"    value={`${m.avgWait}m`}    sub={`${m.total} seatings tracked`} />
        <MetricCard title="Longest Wait"     value={`${m.maxWait}m`}    sub="Single guest" />
        <MetricCard title="Shortest Wait"    value={`${m.minWait}m`}    sub="Single guest" />
        <MetricCard title="Avg Party Size"   value={m.avgPartySize.toFixed(1)} sub="Guests per group" />
        <MetricCard title="Kiosk Self-Joins" value={`${m.kioskPct}%`}   sub="Added via guest kiosk" />
        {m.avgAccuracy != null ? (
          <MetricCard
            title="Quote Accuracy"
            value={`${m.avgAccuracy > 0 ? '+' : ''}${m.avgAccuracy}m avg`}
            sub={
              m.avgAccuracy > 2  ? 'Underquoting — guests wait longer than told' :
              m.avgAccuracy < -2 ? 'Overquoting — guests seated faster than told' :
              'Accurate quoting'
            }
          />
        ) : (
          <MetricCard title="Quote Accuracy" value="—" dim sub={`${m.quotedCount} of ${m.total} had a quote`} />
        )}
      </div>

      {/* ── Wait Time Trend ── */}
      {m.dailyTrend.length > 1 && (
        <ChartCard title="Daily Avg Wait Time (minutes)">
          <ResponsiveContainer width="100%" height={190}>
            <AreaChart data={m.dailyTrend} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} unit="m" />
              <Tooltip content={<ChartTooltipContent formatter={(v) => `${v} min`} />} />
              <Area type="monotone" dataKey="avg" name="Avg Wait" stroke="#5f799a" fill="rgba(95,121,154,0.12)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* ── Day-of-week + Hour-of-day ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ChartCard title="Avg Wait by Day of Week">
          <ResponsiveContainer width="100%" height={165}>
            <BarChart data={m.dowChart} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} unit="m" />
              <Tooltip content={<ChartTooltipContent formatter={(v) => `${v} min`} />} />
              <Bar dataKey="avg" name="Avg Wait" fill="#5f799a" radius={[4,4,0,0]}>
                {m.dowChart.map((entry, i) => (
                  <Cell key={i} fill={entry.avg === Math.max(...m.dowChart.map(d => d.avg)) ? '#4a6380' : '#5f799a'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Avg Wait by Hour of Day">
          <ResponsiveContainer width="100%" height={165}>
            <BarChart data={m.hourChart} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} unit="m" />
              <Tooltip content={<ChartTooltipContent formatter={(v) => `${v} min`} />} />
              <Bar dataKey="avg" name="Avg Wait" fill="#C9A84C" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Party Size ── */}
      <ChartCard title="Party Size Breakdown">
        <div className="grid grid-cols-2 gap-4 mt-1">
          <div className="flex flex-col gap-2">
            <SectionLabel>Guest Count</SectionLabel>
            {m.partySizeChart.map(r => (
              <BarRow key={r.label} label={`Party of ${r.label}`} value={r.count} max={m.total}
                valueStr={`${r.count} (${m.total ? Math.round(r.count / m.total * 100) : 0}%)`} />
            ))}
          </div>
          <div className="flex flex-col gap-2">
            <SectionLabel>Avg Wait per Size</SectionLabel>
            {m.partySizeChart.filter(r => r.count > 0).map(r => (
              <BarRow key={r.label} label={`Party of ${r.label}`} value={r.avgWait}
                max={m.maxWait} valueStr={`${r.avgWait}m avg`} />
            ))}
          </div>
        </div>
      </ChartCard>

      {/* ── Quote Accuracy ── */}
      {m.quotedCount > 0 && (
        <ChartCard title="Quote Accuracy Distribution">
          <p className="text-xs text-neutral-400 mb-3">
            How actual wait compared to quoted wait ({m.quotedCount} of {m.total} seatings had a quote).
            Positive = guest waited longer than told; negative = seated sooner.
          </p>
          <div className="flex flex-col gap-2">
            {m.accuracyBuckets.map(b => (
              <BarRow key={b.label} label={b.label} value={b.count} max={m.quotedCount}
                valueStr={`${b.count} (${m.quotedCount ? Math.round(b.count / m.quotedCount * 100) : 0}%)`} />
            ))}
          </div>
        </ChartCard>
      )}

    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const SUB_TABS = ['Overview', 'Menu', 'Servers', 'Patterns', 'Waitlist'];

const AnalyticsView = ({ restaurantId }) => {
  const now0 = new Date();
  const [rangeIdx,     setRangeIdx]     = useState(1);
  const [monthYear,    setMonthYear]    = useState({ year: now0.getFullYear(), month: now0.getMonth() });
  const [selectedYear, setSelectedYear] = useState(now0.getFullYear());
  const [subTab,       setSubTab]       = useState('Overview');
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState('');
  const [aggData,          setAggData]          = useState(null);
  const [comparePeriods,    setComparePeriods]    = useState([]); // [{key, label, color, dailyChart, monthlyChart}]
  const [loadingCompare,    setLoadingCompare]    = useState(false);
  const [addingMonthYear,   setAddingMonthYear]   = useState({ year: now0.getFullYear(), month: now0.getMonth() });
  const [addingYear,        setAddingYear]        = useState(now0.getFullYear() - 1);

  const currentDays = RANGES[rangeIdx]?.days ?? 7;
  const isMonthMode = currentDays === -1;
  const isYearMode  = currentDays === -2;

  // Derived date range passed to WaitlistTab
  const dateRange = useMemo(() => {
    const days = RANGES[rangeIdx]?.days ?? 7;
    if (days === -1) {
      return {
        startMs: new Date(monthYear.year, monthYear.month, 1).getTime(),
        endMs:   new Date(monthYear.year, monthYear.month + 1, 0, 23, 59, 59, 999).getTime(),
      };
    }
    if (days === -2) {
      return {
        startMs: new Date(selectedYear, 0, 1).getTime(),
        endMs:   new Date(selectedYear, 11, 31, 23, 59, 59, 999).getTime(),
      };
    }
    if (days === 0) {
      const t = new Date();
      return {
        startMs: new Date(t.getFullYear(), t.getMonth(), t.getDate(), 0, 0, 0, 0).getTime(),
        endMs:   new Date(t.getFullYear(), t.getMonth(), t.getDate(), 23, 59, 59, 999).getTime(),
      };
    }
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return { startMs: cutoff.getTime(), endMs: Date.now() };
  }, [rangeIdx, monthYear, selectedYear]);

  // ── Fetch raw data for a date range ───────────────────────────────────────
  const fetchRangeData = useCallback(async (start, end) => {
    const [resSnap, pickupSnap] = await Promise.all([
      getDocs(query(
        collection(db, 'restaurants_data', restaurantId, 'reservations'),
        where('date', '>=', Timestamp.fromDate(start)),
        where('date', '<=', Timestamp.fromDate(end)),
      )),
      getDocs(query(
        collection(db, 'restaurants_data', restaurantId, 'pickup_orders'),
        where('createdAt', '>=', start.getTime()),
        where('createdAt', '<=', end.getTime()),
      )),
    ]);
    return {
      reservations: resSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      pickupOrders: pickupSnap.docs.map(d => ({ id: d.id, ...d.data() })),
    };
  }, [restaurantId]);

  // ── Primary data load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const days = RANGES[rangeIdx]?.days ?? 7;

      let resQuery, pickupQuery, gcQuery;

      if (days === -1) {
        const start = new Date(monthYear.year, monthYear.month, 1);
        const end   = new Date(monthYear.year, monthYear.month + 1, 0, 23, 59, 59, 999);
        resQuery = query(collection(db, 'restaurants_data', restaurantId, 'reservations'), where('date', '>=', Timestamp.fromDate(start)), where('date', '<=', Timestamp.fromDate(end)));
        pickupQuery = query(collection(db, 'restaurants_data', restaurantId, 'pickup_orders'), where('createdAt', '>=', start.getTime()), where('createdAt', '<=', end.getTime()));
        gcQuery = query(collection(db, 'restaurants_data', restaurantId, 'giftCards'), where('createdAt', '>=', Timestamp.fromDate(start)), where('createdAt', '<=', Timestamp.fromDate(end)));
      } else if (days === -2) {
        const start = new Date(selectedYear, 0, 1);
        const end   = new Date(selectedYear, 11, 31, 23, 59, 59, 999);
        resQuery = query(collection(db, 'restaurants_data', restaurantId, 'reservations'), where('date', '>=', Timestamp.fromDate(start)), where('date', '<=', Timestamp.fromDate(end)));
        pickupQuery = query(collection(db, 'restaurants_data', restaurantId, 'pickup_orders'), where('createdAt', '>=', start.getTime()), where('createdAt', '<=', end.getTime()));
        gcQuery = query(collection(db, 'restaurants_data', restaurantId, 'giftCards'), where('createdAt', '>=', Timestamp.fromDate(start)), where('createdAt', '<=', Timestamp.fromDate(end)));
      } else if (days === 0) {
        const now          = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        const endOfToday   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        resQuery = query(collection(db, 'restaurants_data', restaurantId, 'reservations'), where('date', '>=', Timestamp.fromDate(startOfToday)), where('date', '<=', Timestamp.fromDate(endOfToday)));
        pickupQuery = query(collection(db, 'restaurants_data', restaurantId, 'pickup_orders'), where('createdAt', '>=', startOfToday.getTime()), where('createdAt', '<=', endOfToday.getTime()));
        gcQuery = query(collection(db, 'restaurants_data', restaurantId, 'giftCards'), where('createdAt', '>=', Timestamp.fromDate(startOfToday)), where('createdAt', '<=', Timestamp.fromDate(endOfToday)));
      } else {
        const cutoff   = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        resQuery = query(collection(db, 'restaurants_data', restaurantId, 'reservations'), where('date', '>=', Timestamp.fromDate(cutoff)));
        pickupQuery = query(collection(db, 'restaurants_data', restaurantId, 'pickup_orders'), where('createdAt', '>=', cutoff.getTime()));
        gcQuery = query(collection(db, 'restaurants_data', restaurantId, 'giftCards'), where('createdAt', '>=', Timestamp.fromDate(cutoff)));
      }

      const [resSnap, pickupSnap, gcSnap] = await Promise.all([getDocs(resQuery), getDocs(pickupQuery), getDocs(gcQuery)]);
      const reservations = resSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const pickupOrders = pickupSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const giftCards    = gcSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const resData    = aggregate(reservations);
      const pickupData = aggregatePickup(pickupOrders);
      const gcData     = aggregateGiftCards(giftCards);
      const { dailyChart, monthlyChart } = buildPeriodCharts(reservations, pickupOrders);

      setAggData({ ...resData, pickup: pickupData, giftCards: gcData, dailyChart, monthlyChart, currentDays: days });
    } catch (e) {
      console.error('[AnalyticsView]', e);
      setError('Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, rangeIdx, monthYear, selectedYear]);

  useEffect(() => { load(); }, [load]);

  // ── Add comparison period ─────────────────────────────────────────────────
  const addComparisonMonth = async () => {
    const key = `${addingMonthYear.year}-${String(addingMonthYear.month + 1).padStart(2, '0')}`;
    if (comparePeriods.find(p => p.key === key)) return;
    const colorIdx = (comparePeriods.length + 1) % COMPARE_PALETTE.length;
    setLoadingCompare(true);
    try {
      const start = new Date(addingMonthYear.year, addingMonthYear.month, 1);
      const end   = new Date(addingMonthYear.year, addingMonthYear.month + 1, 0, 23, 59, 59, 999);
      const { reservations, pickupOrders } = await fetchRangeData(start, end);
      const { dailyChart, monthlyChart } = buildPeriodCharts(reservations, pickupOrders);
      const stats = { ...aggregate(reservations), pickup: aggregatePickup(pickupOrders) };
      setComparePeriods(prev => [...prev, {
        key,
        label: `${MONTH_SHORT[addingMonthYear.month]} ${addingMonthYear.year}`,
        color: COMPARE_PALETTE[colorIdx],
        dailyChart,
        monthlyChart,
        stats,
      }]);
    } catch (e) { console.error('[compare month]', e); }
    finally { setLoadingCompare(false); }
  };

  const addComparisonYear = async () => {
    const key = `year-${addingYear}`;
    if (comparePeriods.find(p => p.key === key)) return;
    const colorIdx = (comparePeriods.length + 1) % COMPARE_PALETTE.length;
    setLoadingCompare(true);
    try {
      const start = new Date(addingYear, 0, 1);
      const end   = new Date(addingYear, 11, 31, 23, 59, 59, 999);
      const { reservations, pickupOrders } = await fetchRangeData(start, end);
      const { dailyChart, monthlyChart } = buildPeriodCharts(reservations, pickupOrders);
      const stats = { ...aggregate(reservations), pickup: aggregatePickup(pickupOrders) };
      setComparePeriods(prev => [...prev, {
        key,
        label: String(addingYear),
        color: COMPARE_PALETTE[colorIdx],
        dailyChart,
        monthlyChart,
        stats,
      }]);
    } catch (e) { console.error('[compare year]', e); }
    finally { setLoadingCompare(false); }
  };

  const removeComparePeriod = (key) => setComparePeriods(prev => prev.filter(p => p.key !== key));

  // Clear comparisons when switching mode
  const handleRangeChange = (idx) => {
    setRangeIdx(idx);
    setComparePeriods([]);
  };

  // ── Primary label for chart legend ────────────────────────────────────────
  const primaryLabel = isYearMode
    ? String(selectedYear)
    : isMonthMode
      ? `${MONTH_SHORT[monthYear.month]} ${monthYear.year}`
      : RANGES[rangeIdx]?.label || 'Primary';

  // ── Build merged recharts data for Overview charts ────────────────────────
  const mergedRevChart = useMemo(() => {
    if (!aggData) return [];
    const useMonthly = isYearMode;
    const primaryData = useMonthly ? (aggData.monthlyChart || []) : (aggData.dailyChart || []);

    // If no comparison periods, just return primary data with renamed keys
    if (comparePeriods.length === 0) {
      return primaryData.map(d => ({
        ...d,
        [primaryLabel + '_total']: d.total,
        [primaryLabel + '_tips']:  d.tips,
        [primaryLabel + '_avg']:   d.avgCheck,
        _x: useMonthly ? d.label : String(d.day || d.label),
      }));
    }

    // Build lookup maps indexed by comparable key
    const toKey = (d) => useMonthly ? d.label : d.day;
    const primaryMap = {};
    for (const d of primaryData) primaryMap[toKey(d)] = d;

    // Union of all x values
    const allKeys = new Set(primaryData.map(toKey));
    for (const cp of comparePeriods) {
      const cpData = useMonthly ? (cp.monthlyChart || []) : (cp.dailyChart || []);
      for (const d of cpData) allKeys.add(toKey(d));
    }

    const sorted = [...allKeys].sort((a, b) => {
      if (typeof a === 'number' && typeof b === 'number') return a - b;
      // For month labels, sort by MONTH_SHORT index
      const ai = MONTH_SHORT.indexOf(a), bi = MONTH_SHORT.indexOf(b);
      if (ai >= 0 && bi >= 0) return ai - bi;
      return String(a).localeCompare(String(b));
    });

    return sorted.map(k => {
      const obj = { _x: String(k) };
      const pd = primaryMap[k];
      obj[primaryLabel + '_total'] = pd?.total ?? null;
      obj[primaryLabel + '_tips']  = pd?.tips  ?? null;
      obj[primaryLabel + '_avg']   = pd?.avgCheck ?? null;
      for (const cp of comparePeriods) {
        const cpData = useMonthly ? (cp.monthlyChart || []) : (cp.dailyChart || []);
        const cpMap = {};
        for (const d of cpData) cpMap[toKey(d)] = d;
        const cd = cpMap[k];
        obj[cp.label + '_total'] = cd?.total ?? null;
        obj[cp.label + '_tips']  = cd?.tips  ?? null;
        obj[cp.label + '_avg']   = cd?.avgCheck ?? null;
      }
      return obj;
    });
  }, [aggData, comparePeriods, isYearMode, isMonthMode, primaryLabel]);

  // All series (primary + comparisons)
  const allSeries = useMemo(() => {
    const s = [{ label: primaryLabel, color: COMPARE_PALETTE[0] }];
    for (const cp of comparePeriods) s.push({ label: cp.label, color: cp.color });
    return s;
  }, [primaryLabel, comparePeriods]);

  return (
    <div className="space-y-5 max-w-2xl">

      {/* Range picker */}
      <div className="space-y-3">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none -mx-1 px-1">
          {RANGES.map((r, i) => (
            <button
              key={r.label}
              onClick={() => handleRangeChange(i)}
              className={`shrink-0 px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
                rangeIdx === i ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Month mode: month/year picker + comparison controls */}
        {isMonthMode && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Viewing</span>
              <select value={monthYear.month} onChange={e => setMonthYear(prev => ({ ...prev, month: Number(e.target.value) }))}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
                {MONTH_NAMES.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select value={monthYear.year} onChange={e => setMonthYear(prev => ({ ...prev, year: Number(e.target.value) }))}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
                {Array.from({ length: 5 }, (_, i) => now0.getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Compare</span>
              <select value={addingMonthYear.month} onChange={e => setAddingMonthYear(prev => ({ ...prev, month: Number(e.target.value) }))}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
                {MONTH_NAMES.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select value={addingMonthYear.year} onChange={e => setAddingMonthYear(prev => ({ ...prev, year: Number(e.target.value) }))}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
                {Array.from({ length: 5 }, (_, i) => now0.getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button onClick={addComparisonMonth} disabled={loadingCompare}
                className="text-sm px-3 py-1.5 bg-neutral-900 text-white rounded-lg font-semibold hover:opacity-80 disabled:opacity-50 shrink-0">
                {loadingCompare ? '…' : '+ Add'}
              </button>
            </div>
            {comparePeriods.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {comparePeriods.map(cp => (
                  <span key={cp.key} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full text-white"
                    style={{ backgroundColor: cp.color }}>
                    {cp.label}
                    <button onClick={() => removeComparePeriod(cp.key)} className="hover:opacity-70">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Year mode: year picker + comparison controls */}
        {isYearMode && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Viewing</span>
              <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
                {Array.from({ length: 5 }, (_, i) => now0.getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Compare</span>
              <select value={addingYear} onChange={e => setAddingYear(Number(e.target.value))}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
                {Array.from({ length: 5 }, (_, i) => now0.getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button onClick={addComparisonYear} disabled={loadingCompare}
                className="text-sm px-3 py-1.5 bg-neutral-900 text-white rounded-lg font-semibold hover:opacity-80 disabled:opacity-50 shrink-0">
                {loadingCompare ? '…' : '+ Add Year'}
              </button>
            </div>
            {comparePeriods.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {comparePeriods.map(cp => (
                  <span key={cp.key} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full text-white"
                    style={{ backgroundColor: cp.color }}>
                    {cp.label}
                    <button onClick={() => removeComparePeriod(cp.key)} className="hover:opacity-70">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sub-tab bar */}
      <SubTabBar tabs={SUB_TABS} active={subTab} onChange={setSubTab} />

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-sm text-neutral-400 text-center py-12">Loading analytics…</p>
      ) : aggData ? (
        <>
          {subTab === 'Overview'  && <OverviewTab  data={aggData} mergedRevChart={mergedRevChart} allSeries={allSeries} isYearMode={isYearMode} comparePeriods={comparePeriods} primaryLabel={primaryLabel} />}
          {subTab === 'Menu'      && <MenuTab      data={aggData} comparePeriods={comparePeriods} primaryLabel={primaryLabel} />}
          {subTab === 'Servers'   && <ServersTab   data={aggData} comparePeriods={comparePeriods} primaryLabel={primaryLabel} />}
          {subTab === 'Patterns'  && <PatternsTab  data={aggData} comparePeriods={comparePeriods} primaryLabel={primaryLabel} />}
          {subTab === 'Waitlist'  && <WaitlistTab  restaurantId={restaurantId} startMs={dateRange.startMs} endMs={dateRange.endMs} />}
        </>
      ) : null}
    </div>
  );
};

export default AnalyticsView;
